import { RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { BADGED_STATES, EVENT_STATE_LABEL_KEYS } from "~/lib/event-state";
import type { EventState } from "~/types/event";

/**
 * The webinar's state as a small pill — "Živě", "Brzy začínáme", "Proběhlo",
 * "Záznam dostupný".
 *
 * Renders nothing for the resting state: most of the calendar is "Připravujeme", and a
 * badge on every card would say nothing. `always` forces the label out anyway, for the
 * detail screen where the state line is part of the information rather than a flag.
 *
 * Live gets the one colour that means "now"; the rest are quiet, because they describe
 * rather than call.
 */
export function EventStateBadge({
  state,
  always = false,
}: {
  state: EventState;
  always?: boolean;
}) {
  const { t } = useTranslation();
  if (!always && !BADGED_STATES.has(state)) return null;

  const live = state === "live";
  const soon = state === "soon";

  return (
    <Box
      component="span"
      data-testid={`events.state-${state}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        paddingInline: 1.25,
        paddingBlock: 0.375,
        borderRadius: `${RADIUS.full}px`,
        border: "1px solid",
        borderColor: live ? "error.main" : soon ? "primary.main" : "divider",
        color: live ? "error.main" : soon ? "primary.main" : "text.secondary",
        backgroundColor: "background.paper",
        whiteSpace: "nowrap",
      }}
    >
      {live ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "error.main",
            animation: "pulse 1.6s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.35 },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ) : null}
      <Typography
        variant="L3"
        component="span"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {t(EVENT_STATE_LABEL_KEYS[state])}
      </Typography>
    </Box>
  );
}
